/**
 * المرحلة 1.3 — the small pieces every booking screen shares: the scope's
 * booking policies, a channel's badge, a rating, the soonest slot and a day.
 *
 * Every word goes through t(); every side is logical, so the same markup
 * reads right to left in Arabic and left to right in English.
 */

import { useMemo } from 'react';
import { Icon } from '../../components/common';
import { usePermissions } from '../../context/PermissionContext';
import { useI18n } from '../../i18n/I18nProvider';
import { formatTime12 } from '../../domain/dates';
import { MODE_ICONS, bookingPolicy, ratingText } from '../../domain/discovery';

/** The caller's booking policies (GET /api/auth/capabilities locale.booking), whole. */
export function useBookingPolicy() {
  const { locale } = usePermissions();
  return useMemo(() => bookingPolicy(locale), [locale]);
}

/**
 * «الثلاثاء 21 أكتوبر» / «Tuesday 21 October», from a 'YYYY-MM-DD' key. Any
 * Intl parts can be asked for instead (`{ weekday: 'short' }` alone).
 */
export function dayLabel(dateKey, lang = 'ar', parts = { weekday: 'long', day: 'numeric', month: 'long' }) {
  if (!dateKey) return '';
  try {
    return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'ar-EG-u-nu-latn', { timeZone: 'UTC', ...parts }).format(new Date(`${dateKey}T12:00:00Z`));
  } catch {
    return dateKey;
  }
}

/** The arrow that points forward in the reader's direction. */
export const forwardIcon = (lang) => (lang === 'en' ? 'arrow-right' : 'arrow-left');

/** A channel: «حضوري» / «فيديو» / «صوت», with its icon. */
export function ModeBadge({ mode = 'in_person', className = '', compact = false }) {
  const { t } = useI18n();
  const tone = mode === 'video' ? 'mode-badge--video' : mode === 'voice' ? 'mode-badge--voice' : 'mode-badge--in-person';
  return (
    <span className={`mode-badge ${tone} ${className}`} title={t(`discovery.mode.${mode}`)}>
      <Icon name={MODE_ICONS[mode] || 'building-2'} size={12} />
      {!compact && t(`discovery.mode.${mode}`)}
    </span>
  );
}

/** «★ 4.6 · 12 تقييم», or nothing while the policy keeps the number back. */
export function RatingLine({ avg, count, className = '' }) {
  const { t } = useI18n();
  const text = ratingText(avg);
  if (!text) return null;
  return (
    <span className={`rating-line ${className}`}>
      <Icon name="star" size={13} className="rating-line__star" />
      <strong>{text}</strong>
      {count ? <span className="rating-line__count">{t('discovery.card.reviews', { n: count })}</span> : null}
    </span>
  );
}

/** Five stars a patient taps; read-only when `onChange` is absent. */
export function Stars({ value = 0, onChange, size = 22, label }) {
  const { t } = useI18n();
  return (
    <div className="stars" role={onChange ? 'radiogroup' : 'img'} aria-label={label || t('review.stars', { n: value || 0 })}>
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= value;
        const star = <Icon name="star" size={size} className={on ? 'stars__on' : 'stars__off'} />;
        return onChange ? (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={n === value}
            aria-label={t('review.stars', { n })}
            className="stars__btn"
            onClick={() => onChange(n)}
            data-testid={`star-${n}`}
          >
            {star}
          </button>
        ) : (
          <span key={n}>{star}</span>
        );
      })}
    </div>
  );
}

/** «أقرب موعد: الثلاثاء 21 أكتوبر · 10:00 ص», or that there is none in 30 days. */
export function SoonestLine({ soonest, className = '' }) {
  const { t, lang } = useI18n();
  return (
    <span className={`soonest-line ${soonest ? '' : 'soonest-line--none'} ${className}`}>
      <Icon name="clock-3" size={13} />
      {soonest
        ? t('discovery.card.soonest', { day: dayLabel(soonest.date, lang, { weekday: 'short', day: 'numeric', month: 'short' }), time: formatTime12(soonest.time) })
        : t('discovery.card.noSoonest')}
    </span>
  );
}
